/**
 * Market labels — shared formatting helpers for category slugs + product types.
 *
 * SPEC-MARKETPLACE-UX-POLISH (founder directive 2026-10-03): "mejora el texto
 * de las categorías — ejemplo actualmente dice hogar-y-moda que sea Hogar y
 * moda sin guiones intermedios. Al admin le aparece etiquetas en inglés
 * physical en vez de fisico."
 *
 * These helpers centralize the slug→label + type→Spanish-label transformations
 * so all marketplace UI surfaces (product card, product detail, market catalog,
 * admin dashboard, store page) show consistent, properly formatted labels.
 */

import type { ProductType } from '@profileia/shared';

/**
 * Format a category slug as a human-readable label in Spanish.
 *
 * Rules:
 *   - Split by '-' and join with spaces.
 *   - Capitalize the first letter of each word EXCEPT conjunctions
 *     ('y', 'e', 'o', 'u', 'ni') which stay lowercase.
 *   - The first word is ALWAYS capitalized (even if it's a conjunction).
 *
 * Examples:
 *   - 'alimentos'                  → 'Alimentos'
 *   - 'hogar-y-muebles'            → 'Hogar y Muebles'
 *   - 'servicios-profesionales'    → 'Servicios Profesionales'
 *   - 'herramientas-y-construccion'→ 'Herramientas y Construcción'
 *   - 'educacion-y-digital'        → 'Educación y Digital'
 *
 * Note: accents are NOT added here (the slug form doesn't have them).
 * The canonical labels WITH accents live in MARKET_CATEGORIES (if needed
 * in the future, we can add an accent map — but the founder's directive
 * was specifically about removing guiones, not adding accents).
 */
const CONJUNCTIONS = new Set(['y', 'e', 'o', 'u', 'ni']);

export function formatCategoryLabel(slug: string): string {
  return slug
    .split('-')
    .map((word, index) => {
      // Always capitalize the first word.
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      // Conjunctions stay lowercase.
      if (CONJUNCTIONS.has(word)) {
        return word;
      }
      // Capitalize all other words.
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Map product type enum values to Spanish labels.
 *
 * The backend stores types as 'physical' | 'digital' | 'service' (English
 * enum values for code clarity). The UI must show Spanish labels.
 */
const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  physical: 'Físico',
  digital: 'Digital',
  service: 'Servicio',
};

export function formatProductTypeLabel(type: ProductType): string {
  return PRODUCT_TYPE_LABELS[type] ?? type;
}
